import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

export const ModalOverlay = styled.div<{ isOpen: boolean }>`
	position: fixed;
	inset: 0;
	z-index: 99;
	display: flex;
	flex-direction: column;
	height: 100dvh;
	overflow: hidden;
	background-color: ${(props) => props.theme.ui.surface.page};
	opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
	visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
	transition:
		opacity ${(props) => props.theme.effects.transitionBase},
		visibility ${(props) => props.theme.effects.transitionBase};
`;

export const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex-shrink: 0;
	height: ${(props) => props.theme.heights.headerMobile};
	padding: 0 ${(props) => props.theme.scale.spaceLg};
	border-bottom: 1px solid ${(props) => props.theme.ui.border.default};
`;

export const CloseButton = styled.button`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 4rem;
	height: 4rem;
	background: transparent;
	border: none;
	cursor: pointer;

	&::before,
	&::after {
		content: '';
		position: absolute;
		width: 2rem;
		height: 2px;
		background: ${(props) => props.theme.ui.text.muted};
	}

	&::before {
		transform: rotate(45deg);
	}

	&::after {
		transform: rotate(-45deg);
	}
`;

export const ModalContent = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;
	overflow-x: hidden;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	padding: 0 ${(props) => props.theme.scale.spaceLg} ${(props) => props.theme.scale.space2xl};
`;

export const AuthBar = styled.div`
	flex-shrink: 0;
	padding: ${(props) => props.theme.scale.spaceLg} 0;
`;

const authButtonStyles = (props: { theme: import('@emotion/react').Theme }) => css`
	width: 100%;
	min-height: 4.4rem;
	padding: 0 ${props.theme.scale.spaceMd};
	border-radius: ${props.theme.effects.radiusMd};
	font-size: ${props.theme.fontSize.sizeMd};
	font-weight: ${props.theme.fontWeight.weightSemi};
	font-family: inherit;
	cursor: pointer;
	transition:
		background-color ${props.theme.effects.transitionFast},
		border-color ${props.theme.effects.transitionFast};
`;

export const LoginButton = styled.button`
	${authButtonStyles}
	border: 1px solid ${(props) => props.theme.ui.interactive.primary};
	background-color: ${(props) => props.theme.ui.interactive.primary};
	color: ${(props) => props.theme.ui.text.inverse};

	&:hover {
		background-color: ${(props) => props.theme.colors.primary400};
		border-color: ${(props) => props.theme.colors.primary400};
	}
`;

export const MypageButton = styled.button`
	${authButtonStyles}
	border: 1px solid ${(props) => props.theme.ui.interactive.primary};
	background-color: ${(props) => props.theme.ui.surface.page};
	color: ${(props) => props.theme.ui.interactive.primary};
	display: flex;
	align-items: center;
	justify-content: center;
	gap: ${(props) => props.theme.scale.space3xs};

	&:hover {
		background-color: ${(props) => props.theme.ui.interactive.primaryMuted};
	}
`;

export const MenuList = styled.ul`
	flex: 1;
	display: flex;
	flex-direction: column;
	margin: 0;
	padding: 0;
	list-style: none;
	border: 1px solid ${(props) => props.theme.ui.border.default};
	border-radius: ${(props) => props.theme.effects.radiusMd};
	overflow: hidden;
`;

export const MenuItem = styled.li`
	width: 100%;
	border-bottom: 1px solid ${(props) => props.theme.ui.border.default};
`;

const mainMenuStyles = (props: { theme: import('@emotion/react').Theme }) => css`
	display: flex;
	align-items: center;
	width: 100%;
	min-height: 5.2rem;
	padding: ${props.theme.scale.spaceSm} ${props.theme.scale.spaceMd};
	color: ${props.theme.ui.text.primary};
	font-size: ${props.theme.fontSize.sizeMd};
	font-weight: ${props.theme.fontWeight.weightSemi};
	line-height: ${props.theme.ui.lineHeight.heading};
	letter-spacing: ${props.theme.ui.letterSpacing.tight};
	text-align: left;
	text-decoration: none;
	cursor: pointer;
	box-sizing: border-box;

	&:hover {
		background-color: ${props.theme.ui.interactive.hoverWash};
	}
`;

export const MainMenuLink = styled(Link)`
	${mainMenuStyles}
`;

export const MainMenuButton = styled.button`
	${mainMenuStyles}
	border: none;
	background: transparent;
	font-family: inherit;
`;

export const MainMenuTrigger = styled.span`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
	gap: ${(props) => props.theme.scale.spaceSm};
`;

export const MainMenuArrow = styled.span<{ $open: boolean }>`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	color: ${(props) => props.theme.ui.text.muted};
	transition: transform ${(props) => props.theme.effects.transitionFast};
	transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

export const SubMenuList = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	background-color: ${(props) => props.theme.ui.surface.subtle};
`;

export const SubMenuItem = styled.li`
	border-top: 1px solid ${(props) => props.theme.ui.border.default};
`;

export const SubMenuButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	min-height: 4.8rem;
	padding: ${(props) => props.theme.scale.spaceSm} ${(props) => props.theme.scale.spaceMd};
	padding-left: calc(${(props) => props.theme.scale.spaceMd} + ${(props) => props.theme.scale.spaceLg});
	border: none;
	background: transparent;
	color: ${(props) => props.theme.ui.text.secondary};
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	font-family: inherit;
	text-align: left;
	cursor: pointer;

	&:hover {
		color: ${(props) => props.theme.ui.text.primary};
		background-color: ${(props) => props.theme.ui.interactive.hoverWash};
	}
`;
